
import { useState } from "react";

import { FilledButton, OutlinedButton } from "../../common/Button";
import useCurrentUser from "../../../hooks/useCurrentUser";
import Lightbox from "../../common/Lightbox";
import { IEmployer, NumberEmployees, RevenueRange, StartupStage, TypeOfBusiness } from "../../../data_models/employer";
import MultiTextSelect from "../../common/MultiTextSelect";
import { OutlinedMultiDropdownMenu } from "../../common/MultiDropdownMenu";
import { getNumericEnumValues, getStringEnumValues } from "../../../utils/enumUtils";
import { UserEnumTextMapper } from "../../../data_models/enumToTextMapper";
import { OutlinedDropdownMenu } from "../../common/DropdownMenu";
import { set } from "react-datepicker/dist/date_utils";
import RichTextEditor from "../../common/RichTextEditor";


interface EditCompanyInfoLightboxProps {
    onClose: () => void;
}

export const EditCompanyInfoLightbox: React.FC<EditCompanyInfoLightboxProps> = ({ onClose }) => {

    const { currentUser, updateCurrentUser } = useCurrentUser();
    const [loading, setLoading] = useState(false);

    const [bio, setBio] = useState((currentUser as IEmployer)?.bio || '');
    const [industries, setIndustries] = useState<string[]>((currentUser as IEmployer)?.industries || []);
    const [typesOfBusiness, setTypesOfBusiness] = useState<TypeOfBusiness[]>((currentUser as IEmployer)?.typesOfBusiness || []);

    // private
    const [numberEmployees, setNumberEmployees] = useState((currentUser as IEmployer)?.numberEmployees || NumberEmployees.EMPLOYEES_0);
    const [revenueRange, setRevenueRange] = useState((currentUser as IEmployer)?.revenueRange || RevenueRange.NO_INFO);
    const [startupStage, setStartupStage] = useState((currentUser as IEmployer)?.startupStage || StartupStage.NONE);


    const onSubmit = async () => {
        setLoading(true);
        try {

            const updateObj: IEmployer = {
                bio: bio,
                industries: industries,
                typesOfBusiness: typesOfBusiness,

                numberEmployees: numberEmployees,
                revenueRange: revenueRange,
                startupStage: startupStage,
            }

            await updateCurrentUser(updateObj);
        } catch (error: any) {
            console.error(error);
        } finally {
            setLoading(false);
            onClose();
        }
    }

    return (
        <Lightbox
            content={
                <div className="flex flex-col space-y-6 w-full" >
                    <h4>Update Company Information</h4>

                    {/* Bio */}
                    <RichTextEditor
                        label="Bio"
                        value={bio}
                        onChange={(content) => setBio(content)}
                        placeholder="Let us know a little about yourself"
                    />

                    {/* Industries */}
                    <MultiTextSelect
                        elements={industries}
                        setElements={setIndustries}
                        label="Industries in which you are active"
                    />

                    {/* Business Types */}
                    <OutlinedMultiDropdownMenu
                        options={getNumericEnumValues(TypeOfBusiness)}
                        optionsToTextMapper={UserEnumTextMapper.typeOfBusiness}
                        elements={typesOfBusiness}
                        setElements={setTypesOfBusiness}
                        label="Select what applies to you"

                        maxNumSelections={3}
                    />

                    <div className="flex flex-col space-y-6 p-2 w-full bg-neutral-100 rounded-xl">
                        <p className="caption2 ">Private Information - This information helps us to provide better solutions. We use it amongst others for better matchmaking. It won't be shared with anyone and is only visible to you.</p>

                        {/* Number Employes */}
                        <OutlinedDropdownMenu
                            options={getNumericEnumValues(StartupStage)}
                            optionsToTextMapper={UserEnumTextMapper.startupStage}
                            onSelect={(value) => {
                                const val = value as unknown as StartupStage;
                                setStartupStage(val)
                            }}
                            defaultValue={(currentUser as IEmployer)?.startupStage || StartupStage.NONE}
                            label="Stage of your Startup (if you are one)"
                        />

                        {/* Number Employes */}
                        <OutlinedDropdownMenu
                            options={getStringEnumValues(NumberEmployees)}
                            onSelect={(value) => {
                                const val = value as unknown as string;
                                setNumberEmployees(val)
                            }}
                            defaultValue={(currentUser as IEmployer)?.numberEmployees || NumberEmployees.EMPLOYEES_0}
                            label="Number Employees"
                        />


                        {/* Revenue Range */}
                        <OutlinedDropdownMenu
                            options={getStringEnumValues(RevenueRange)}
                            onSelect={(value) => {
                                const val = value as unknown as string;
                                setRevenueRange(val)
                            }}
                            defaultValue={(currentUser as IEmployer)?.revenueRange || RevenueRange.NO_INFO}
                            label="Revenue Range"
                        />
                    </div>

                    <div className="flex flex-row justify-end w-full space-x-2">
                        <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
                        <FilledButton
                            isLoading={loading}
                            onClick={onSubmit}>Submit</FilledButton>
                    </div>
                </div>}
            onClose={onClose}
        />
    );
}

